import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Funnel, X } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import ProductCard from '@/components/custom/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Apparel', 'Jewelry', 'Homeware', 'Accessories', 'Bags', 'Footwear'];

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [minScore, setMinScore] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products?`;
        if (category !== 'All') url += `category=${category}&`;
        if (minScore > 0) url += `min_score=${minScore}`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, minScore]);

  const clearFilters = () => {
    setCategory('All');
    setMinScore(0);
  };

  const hasActiveFilters = category !== 'All' || minScore > 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Badge variant="outline" className="mb-4">Our Collection</Badge>
          <h1 className="text-4xl md:text-5xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sustainable Products
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Each piece tells a story of heritage, craftsmanship, and positive impact. 
            Filter by category or transparency score to find your perfect match.
          </p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8" data-testid="filter-bar">
            <div className="flex items-center gap-4">
              {/* Category Dropdown */}
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Score Filter Button */}
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="score-filter-btn"
              >
                <Funnel weight="duotone" className="w-4 h-4" />
                Impact Score
                {minScore > 0 && (
                  <Badge className="ml-1 bg-primary text-primary-foreground">{minScore}+</Badge>
                )}
              </Button>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              {products.length} product{products.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Score Slider (Collapsible) */}
          {showFilters && (
            <div className="bg-secondary/50 rounded-xl p-6 mb-8 animate-fade-in-up">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium">Minimum Transparency Score</span>
                <span className="text-sm text-primary font-semibold">{minScore}</span>
              </div>
              <Slider
                value={[minScore]}
                onValueChange={([value]) => setMinScore(value)}
                max={100}
                step={5}
                className="w-full"
                data-testid="score-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>0</span>
                <span>50</span>
                <span>100</span>
              </div>
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="aspect-[4/5] bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-lg text-muted-foreground mb-4">No products found matching your criteria.</p>
              <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
